import type { DeckCardInput } from '../../../deck/types'

type PlayAudioRequest = {
  /** Player root; audio elements are searched below it. */
  root: HTMLElement | null
  /** Read on every attempt: the card may still be changing. */
  activeCardId: () => string | null
  /** The card model currently shown, used to verify the DOM shows the right card. */
  card: () => DeckCardInput | null
  cardChangeAnimation: string
  index: number
  waitForAudio: boolean
  /** False once the flow restarted; then nothing may run any more. */
  isCurrent: () => boolean
  proceed: () => void
}

const MAX_MOUNT_WAIT_ATTEMPTS = 16
const MOUNT_WAIT_MS = 25

/** The first retry waits for the card-change transition to finish mounting the new card. */
const firstRetryDelayMs = (animation: string) => {
  if (animation === 'none') return MOUNT_WAIT_MS
  if (animation === 'fade' || animation === 'scale') return 230
  if (animation === 'swipe-up' || animation === 'swipe-down') return 260
  if (animation === 'flip' || animation === 'slide-left' || animation === 'slide-right') return 280
  if (animation === 'reveal-under') return 280
  return 260
}

const comparable = (src: string): string => {
  try {
    return new URL(src, window.location.href).href
  } catch {
    return src
  }
}

/** Audio sources the card model has on one face (or both). */
const modelAudioSources = (card: DeckCardInput | null, face: 'front' | 'back' | 'all') => {
  if (!card) return new Set<string>()
  const pick = (which: 'front' | 'back') =>
    (which === 'front' ? card.frontElements : card.backElements) ?? []
  const elements = face === 'all' ? [...pick('front'), ...pick('back')] : pick(face)
  return new Set(
    elements
      .map((el) => {
        if (!el || typeof el !== 'object') return ''
        const record = el as Record<string, unknown>
        return record.type === 'audio' && typeof record.src === 'string' ? record.src.trim() : ''
      })
      .filter((src) => src.length > 0)
      .map(comparable),
  )
}

/** Audio elements of the active card's visible face (falling back to the card, then all). */
function findAudio(request: PlayAudioRequest) {
  const root = request.root
  const activeCardId = request.activeCardId()
  const all = Array.from(root?.querySelectorAll('audio') ?? []) as HTMLAudioElement[]
  const host =
    typeof activeCardId === 'string' && activeCardId.length > 0
      ? Array.from(root?.querySelectorAll('[data-pfc-stage-card-id]') ?? []).find(
          (el) => el.getAttribute('data-pfc-stage-card-id') === activeCardId,
        )
      : null
  const backVisible = host?.querySelector('.card-inner')?.classList.contains('flipped') === true
  const visible = host
    ? (Array.from(
        host.querySelectorAll(backVisible ? '.face.back audio' : '.face.front audio'),
      ) as HTMLAudioElement[])
    : []
  const candidates =
    visible.length > 0
      ? visible
      : host
        ? (Array.from(host.querySelectorAll('audio')) as HTMLAudioElement[])
        : all
  return {
    element: candidates[request.index],
    visibleFace: backVisible ? 'back' : 'front',
  } as const
}

/** Waits for playback to end; external pauses (e.g. face changes) resume playback. */
function playAndWait(audio: HTMLAudioElement, request: PlayAudioRequest) {
  const cleanup = () => {
    audio.removeEventListener('ended', onEnded)
    audio.removeEventListener('error', onError)
    audio.removeEventListener('pause', onPause)
  }
  const finish = () => {
    cleanup()
    request.proceed()
  }
  const onEnded = () => finish()
  const onError = () => finish()
  const onPause = () => {
    if (!request.isCurrent()) {
      cleanup()
      return
    }
    // End-of-track pause is terminal; resuming would replay from the start.
    if (audio.ended) {
      finish()
      return
    }
    // AudioElement pauses audio on hidden faces; resume so wait mode still completes.
    audio.play().catch(() => finish())
  }
  // Attach listeners before play() so short audio cannot end unnoticed.
  audio.addEventListener('ended', onEnded)
  audio.addEventListener('error', onError)
  audio.addEventListener('pause', onPause)

  let playPromise: Promise<void> | undefined
  try {
    playPromise = audio.play()
  } catch {
    finish()
    return
  }
  playPromise?.catch(() => finish())
}

/**
 * Plays audio element `index` of the active card. Retries while the card is still
 * mounting or the DOM still shows the previous card, then skips rather than playing
 * the wrong audio. Background mode advances immediately.
 */
export function playCardAudio(request: PlayAudioRequest) {
  const firstRetryMs = firstRetryDelayMs(request.cardChangeAnimation)
  const tryPlay = (attempt: number) => {
    if (!request.isCurrent()) return
    const { element, visibleFace } = findAudio(request)
    const retryOrSkip = () => {
      if (attempt < MAX_MOUNT_WAIT_ATTEMPTS) {
        const delay = attempt === 0 ? firstRetryMs : MOUNT_WAIT_MS
        window.setTimeout(() => tryPlay(attempt + 1), delay)
        return
      }
      request.proceed()
    }
    if (!element) {
      retryOrSkip()
      return
    }
    const card = request.card()
    const expectedOnFace = modelAudioSources(card, visibleFace)
    const selected = comparable(element.src)
    const matches =
      expectedOnFace.size > 0
        ? expectedOnFace.has(selected)
        : modelAudioSources(card, 'all').has(selected)
    if (!matches) {
      retryOrSkip()
      return
    }
    element.currentTime = 0
    if (!request.waitForAudio) {
      element.play().catch(() => {})
      request.proceed()
      return
    }
    playAndWait(element, request)
  }
  tryPlay(0)
}
